// src/components/Clients.js
import React, { useState, useEffect, useMemo } from "react";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { Dialog } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import {
  Users,
  Building2,
  MapPin,
  Phone,
  Mail,
  Briefcase,
  DollarSign,
  CheckCircle,
  Clock,
  AlertCircle,
  Activity,
  ChevronRight,
  Edit3,
  FileText,
  User,
  ChevronDown,
  Calendar,
  ListTodo,
  PoundSterling,
  X,
} from "lucide-react";
import EditClient from "./EditClient";
import { useTheme } from "../ThemeContext";
import { usePermissions } from "../permissions/PermissionsProvider";
import { ACTIONS } from "../permissions/definition";
import HeaderActionsMinimal from "./HeaderActionsMinimal";
import { evaluateProjectStatuses } from "../utils/status";
import { buildTasksSearchText } from "../utils/tasksSearchText";
import { createNoBackdropCloseHandler } from "../utils/dialogClose";

// ==================== HELPER COMPONENTS ====================
const StatCard = ({ title, value, icon, subtitle }) => (
  <div className="group relative bg-white/[0.08] backdrop-blur-xl rounded-2xl p-5 border border-white/[0.15] min-w-[140px] overflow-hidden transition-all duration-300 hover:bg-white/[0.12] hover:border-white/25 hover:scale-[1.02] hover:shadow-lg">
    <div className="absolute inset-0 bg-gradient-to-br from-white/[0.05] to-transparent pointer-events-none" />
    <div className="relative z-10">
      <div className="flex items-center gap-2.5 mb-2">
        <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center">
          <span className="text-white/80">{icon}</span>
        </div>
        <span className="text-[11px] font-semibold text-white/70 uppercase tracking-widest">
          {title}
        </span>
      </div>
      <p className="text-3xl font-bold text-white tracking-tight">{value}</p>
      {subtitle && <p className="text-xs text-white/50 mt-1">{subtitle}</p>}
    </div>
  </div>
);

const getCountryFlag = (countryName) => {
  const flags = {
    "United States": "🇺🇸",
    "USA": "🇺🇸",
    "UK": "🇬🇧",
    "United Kingdom": "🇬🇧",
    "Germany": "🇩🇪",
    "France": "🇫🇷",
    "Japan": "🇯🇵",
    "China": "🇨🇳",
    "India": "🇮🇳",
    "Brazil": "🇧🇷",
    "Canada": "🇨🇦",
    "Australia": "🇦🇺",
    "Italy": "🇮🇹",
    "Spain": "🇪🇸",
    "Russia": "🇷🇺",
    "South Korea": "🇰🇷",
    "Netherlands": "🇳🇱",
    "Turkey": "🇹🇷",
    "Saudi Arabia": "🇸🇦",
    "Switzerland": "🇨🇭",
    "Sweden": "🇸🇪",
    "Poland": "🇵🇱",
    "Belgium": "🇧🇪",
    "Norway": "🇳🇴",
    "Austria": "🇦🇹",
    "Denmark": "🇩🇰",
    "Finland": "🇫🇮",
    "Ireland": "🇮🇪",
    "New Zealand": "🇳🇿",
    "Singapore": "🇸🇬",
    "UAE": "🇦🇪",
    "United Arab Emirates": "🇦🇪",
    "Israel": "🇮🇱",
    "South Africa": "🇿🇦",
    "Mexico": "🇲🇽",
    "Egypt": "🇪🇬",
    "Morocco": "🇲🇦",
    "Algeria": "🇩🇿",
    "Tunisia": "🇹🇳",
    "Libya": "🇱🇾",
    "Qatar": "🇶🇦",
    "Kuwait": "🇰🇼",
    "Bahrain": "🇧🇭",
    "Oman": "🇴🇲",
    "Jordan": "🇯🇴",
    "Lebanon": "🇱🇧",
    "Iraq": "🇮🇶",
    "Yemen": "🇾🇪",
    "Syria": "🇸🇾",
    "Palestine": "🇵🇸",
    "Iran": "🇮🇷",
    "Pakistan": "🇵🇰",
    "Afghanistan": "🇦🇫",
    "Djibouti": "🇩🇯",
    "Mauritania": "🇲🇷",
    "Somalia": "🇸🇴",
    "Sudan": "🇸🇩",
    "UAE - Abu Dhabi": "🇦🇪",
    "UAE - Ajman": "🇦🇪",
    "UAE - Dubai": "🇦🇪",
    "UAE - Fujairah": "🇦🇪",
    "UAE - RAK": "🇦🇪",
    "UAE - Sharjah": "🇦🇪",
    "UAE - UAQ": "🇦🇪",
  };
  return flags[countryName] || "🌍";
};

// Circle flag helpers (HatScripts CDN) for project cards
const circleFlagCodeMap = {
  "UK": "gb",
  "UNITED KINGDOM": "gb",
  "USA": "us",
  "UNITED STATES": "us",
  "GERMANY": "de",
  "FRANCE": "fr",
  "JAPAN": "jp",
  "CHINA": "cn",
  "INDIA": "in",
  "BRAZIL": "br",
  "CANADA": "ca",
  "AUSTRALIA": "au",
  "ITALY": "it",
  "SPAIN": "es",
  "RUSSIA": "ru",
  "SOUTH KOREA": "kr",
  "NETHERLANDS": "nl",
  "TURKEY": "tr",
  "SAUDI ARABIA": "sa",
  "SWITZERLAND": "ch",
  "SWEDEN": "se",
  "POLAND": "pl",
  "BELGIUM": "be",
  "NORWAY": "no",
  "AUSTRIA": "at",
  "DENMARK": "dk",
  "FINLAND": "fi",
  "IRELAND": "ie",
  "NEW ZEALAND": "nz",
  "SINGAPORE": "sg",
  "UAE": "ae",
  "UNITED ARAB EMIRATES": "ae",
  "UAE - ABU DHABI": "ae",
  "UAE - AJMAN": "ae",
  "UAE - DUBAI": "ae",
  "UAE - FUJAIRAH": "ae",
  "UAE - RAK": "ae",
  "UAE - SHARJAH": "ae",
  "UAE - UAQ": "ae",
  "ISRAEL": "il",
  "SOUTH AFRICA": "za",
  "MEXICO": "mx",
  "EGYPT": "eg",
  "MOROCCO": "ma",
  "ALGERIA": "dz",
  "TUNISIA": "tn",
  "LIBYA": "ly",
  "QATAR": "qa",
  "KUWAIT": "kw",
  "BAHRAIN": "bh",
  "OMAN": "om",
  "JORDAN": "jo",
  "LEBANON": "lb",
  "IRAQ": "iq",
  "YEMEN": "ye",
  "SYRIA": "sy",
  "PALESTINE": "ps",
  "IRAN": "ir",
  "PAKISTAN": "pk",
  "AFGHANISTAN": "af",
  "DJIBOUTI": "dj",
  "MAURITANIA": "mr",
  "SOMALIA": "so",
  "SUDAN": "sd",
};

const getCircleFlagCode = (countryOrCompany) => {
  if (!countryOrCompany) return "xx";
  const normalized = countryOrCompany.trim().toUpperCase();

  if (/^[A-Z]{2}$/.test(normalized)) {
    return normalized.toLowerCase();
  }

  return circleFlagCodeMap[normalized] || "xx";
};

const getCircleFlagUrl = (countryOrCompany) =>
  `https://hatscripts.github.io/circle-flags/flags/${getCircleFlagCode(
    countryOrCompany
  )}.svg`;

const CountryFlagIcon = ({ country, size = 18, className = "" }) => (
  <img
    src={getCircleFlagUrl(country)}
    alt={country ? `${country} flag` : "Flag"}
    width={size}
    height={size}
    className={`flex-shrink-0 ${className}`}
    style={{
      borderRadius: "50%",
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.12)",
      border: "1px solid rgba(15, 23, 42, 0.08)",
      backgroundColor: "#fff",
    }}
    loading="lazy"
  />
);

const STATUS_ORDER = [
  "In Progress",
  "Completed",
  "Overdue",
  "Attention",
  "To be invoiced",
  "Invoiced",
  "Paid",
];

const getStatusConfig = (status) => {
  switch (status) {
    case "Completed":
      return {
        bg: "bg-sky-100",
        text: "text-sky-700",
        border: "border-sky-300",
        icon: <CheckCircle className="w-3.5 h-3.5" />,
        dot: "bg-transparent",
        dotColor: "#90ee90",
        filterDotColor: "#90ee90",
      };
    case "Overdue":
      return {
        bg: "bg-red-100",
        text: "text-red-700",
        border: "border-red-300",
        icon: <AlertCircle className="w-3.5 h-3.5" />,
        dot: "bg-transparent",
        dotColor: "#f08080",
        filterDotColor: "#f08080",
      };
    case "Attention":
      return {
        bg: "bg-yellow-100",
        text: "text-yellow-700",
        border: "border-yellow-300",
        icon: <Clock className="w-3.5 h-3.5" />,
        dot: "bg-transparent",
        dotColor: "#f6d20e",
        filterDotColor: "#f6d20e",
      };
    case "In Progress":
      return {
        bg: "bg-green-100",
        text: "text-green-700",
        border: "border-green-300",
        icon: <Activity className="w-3.5 h-3.5" />,
        dot: "bg-transparent",
        dotColor: "#6798d9",
        filterDotColor: "#6798d9",
      };
    case "To be invoiced":
      return {
        bg: "bg-pink-100",
        text: "text-pink-700",
        border: "border-pink-300",
        icon: <FileText className="w-3.5 h-3.5" />,
        dot: "bg-transparent",
        dotColor: "#f9a8d4",
        filterDotColor: "#f9a8d4",
      };
    case "Invoiced":
      return {
        bg: "bg-purple-100",
        text: "text-purple-700",
        border: "border-purple-300",
        icon: <CheckCircle className="w-3.5 h-3.5" />,
        dot: "bg-transparent",
        dotColor: "#ac95f1",
        filterDotColor: "#ac95f1",
      };
    case "Paid":
      return {
        bg: "bg-emerald-100",
        text: "text-emerald-700",
        border: "border-emerald-300",
        icon: <CheckCircle className="w-3.5 h-3.5" />,
        dot: "bg-transparent",
        dotColor: "#10b981",
        filterDotColor: "#10b981",
      };
    default:
      return {
        bg: "bg-gray-100",
        text: "text-gray-700",
        border: "border-gray-300",
        icon: <Activity className="w-3.5 h-3.5" />,
        dot: "bg-transparent",
        dotColor: "#9ca3af",
        filterDotColor: "#9ca3af",
      };
  }
};

const formatCurrency = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return "£0";
  return `£${num.toLocaleString("en-GB", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

const formatCurrencyNumeric = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return "0";
  return num.toLocaleString("en-GB", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const toDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (value?.seconds) return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateDisplay = (value) => {
  const date = toDate(value);
  if (!date) return null;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const getRelativeDue = (date) => {
  if (!date) return null;
  const now = new Date();
  const diffDays = Math.round((date - now) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Due today";
  if (diffDays === 1) return "Due tomorrow";
  if (diffDays === -1) return "1 day overdue";
  if (diffDays > 1) return `Due in ${diffDays} days`;
  return `${Math.abs(diffDays)} days overdue`;
};

const getNextDueDate = (project) => {
  const dates =
    project?.tasks
      ?.map((task) => toDate(task.dueDate || task.orderAccepted))
      .filter(Boolean) || [];
  if (!dates.length) return null;
  return new Date(Math.min(...dates.map((d) => d.getTime())));
};

const getLatestCompletionDate = (project) => {
  const dates =
    project?.tasks?.map((task) => toDate(task.completedOn)).filter(Boolean) ||
    [];
  if (!dates.length) return null;
  return new Date(Math.max(...dates.map((d) => d.getTime())));
};

const getDaysAgo = (date) => {
  if (!date) return null;
  const now = new Date();
  const diffDays = Math.round((now - date) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "today";
  if (diffDays === 1) return "1 day ago";
  return `${diffDays} days ago`;
};

const getTopCountry = (project) => {
  const counts = {};
  (project?.tasks || []).forEach((t) => {
    if (!t.country) return;
    counts[t.country] = (counts[t.country] || 0) + 1;
  });
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return sorted[0]?.[0];
};

// ==================== MAIN COMPONENT ====================
const Clients = ({ user }) => {
  const [client, setClient] = useState("");
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [clientInfo, setClientInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [lastAppliedClientFromQuery, setLastAppliedClientFromQuery] =
    useState("");

  const [searchParams] = useSearchParams();
  const db = getFirestore();
  const { theme } = useTheme();
  const { can } = usePermissions();
  const canViewFinancials = can(ACTIONS.FINANCIALS_VIEW);
  const openProjectInNewTab = (projectId) => {
    if (!projectId) return;
    window.open(`/project/${projectId}`, "_blank", "noopener,noreferrer");
  };

  // Fetch clients
  useEffect(() => {
    const fetchClients = async () => {
      setLoading(true);
      try {
        const clientsCollection = collection(db, "clients");
        const clientsSnapshot = await getDocs(clientsCollection);
        const clientsList = clientsSnapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().clientName,
        }));
        clientsList.sort((a, b) => a.name.localeCompare(b.name));
        setClients(clientsList);
      } catch (error) {
        console.error("Error fetching clients:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, [db]);

  // Fetch projects when client changes
  useEffect(() => {
    const fetchProjects = async () => {
      if (client) {
        try {
          const projectsCollection = collection(db, "projects");
          const projectsSnapshot = await getDocs(projectsCollection);
          const fetchedProjects = [];
          const updatePromises = [];

          for (const projectDoc of projectsSnapshot.docs) {
            const rawProject = { id: projectDoc.id, ...projectDoc.data() };
            if (rawProject.client !== client) continue;

            const {
              updatedTasks,
              projectStatus,
              invoiceRaised,
              paymentStatus,
            } = evaluateProjectStatuses(rawProject);

            const originalTasks = rawProject.tasks || [];
            const tasksChanged =
              originalTasks.length !== updatedTasks.length ||
              updatedTasks.some(
                (task, index) =>
                  task.taskStatus !==
                  (originalTasks[index] && originalTasks[index].taskStatus)
              );

            const updates = {};
            if (tasksChanged) {
              updates.tasks = updatedTasks;
              updates.tasksSearchText = buildTasksSearchText(updatedTasks);
            }
            if (rawProject.projectStatus !== projectStatus) {
              updates.projectStatus = projectStatus;
            }
            if (rawProject.invoiceRaised !== invoiceRaised) {
              updates.invoiceRaised = invoiceRaised;
            }
            if (rawProject.paymentStatus !== paymentStatus) {
              updates.paymentStatus = paymentStatus || null;
            }

            if (Object.keys(updates).length > 0) {
              updatePromises.push(
                updateDoc(doc(db, "projects", rawProject.id), updates)
              );
            }

            fetchedProjects.push({
              ...rawProject,
              tasks: updatedTasks,
              projectStatus,
              invoiceRaised,
              paymentStatus,
            });
          }

          if (updatePromises.length) {
            await Promise.allSettled(updatePromises);
          }

          setProjects(fetchedProjects);
        } catch (error) {
          console.error("Error fetching projects:", error);
        }
      }
    };
    fetchProjects();
  }, [client, db]);

  const fetchClientInfo = async (clientId) => {
    try {
      const clientDoc = await getDoc(doc(db, "clients", clientId));
      if (clientDoc.exists()) {
        setClientInfo(clientDoc.data());
      }
    } catch (error) {
      console.error("Error fetching client info:", error);
    }
  };

  useEffect(() => {
    if (!clients.length) return;

    const clientFromQuery = searchParams.get("client")?.trim() || "";

    if (clientFromQuery !== lastAppliedClientFromQuery) {
      const matchedClient = clientFromQuery
        ? clients.find((c) => c.name === clientFromQuery)
        : null;

      if (matchedClient && matchedClient.id !== selectedClientId) {
        setClient(matchedClient.name);
        setSelectedClientId(matchedClient.id);
        fetchClientInfo(matchedClient.id);
        setLastAppliedClientFromQuery(clientFromQuery);
        return;
      }

      setLastAppliedClientFromQuery(clientFromQuery);
    }

    if (!selectedClientId && clients[0]) {
      setClient(clients[0].name);
      setSelectedClientId(clients[0].id);
      fetchClientInfo(clients[0].id);
    }
  }, [clients, searchParams, selectedClientId, lastAppliedClientFromQuery]);

  const handleClientChange = (clientObj) => {
    setClient(clientObj.name);
    setSelectedClientId(clientObj.id);
    fetchClientInfo(clientObj.id);
    setIsDropdownOpen(false);
  };

  const handleEditClientClick = () => {
    if (selectedClientId) {
      setOpenEdit(true);
    }
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    if (selectedClientId) {
      fetchClientInfo(selectedClientId);
    }
  };

  // Calculate stats
  const stats = useMemo(() => {
    const totalProjects = projects.length;
    const totalTasks = projects.reduce(
      (sum, p) => sum + (p.tasks?.length || 0),
      0
    );
    const totalIncome = projects.reduce(
      (sum, p) => sum + (parseFloat(p.income) || 0),
      0
    );
    const totalCost = projects.reduce(
      (sum, p) => sum + (parseFloat(p.cost) || 0),
      0
    );

    const statusCounts = STATUS_ORDER.reduce((acc, status) => {
      acc[status] = 0;
      return acc;
    }, {});

    projects.forEach((p) => {
      if (statusCounts[p.projectStatus] !== undefined) {
        statusCounts[p.projectStatus]++;
      }
    });

    return {
      totalProjects,
      totalTasks,
      totalIncome,
      totalCost,
      statusCounts,
    };
  }, [projects]);

  // Build full address
  const fullAddress = useMemo(() => {
    const parts = [
      clientInfo.addressLine1,
      clientInfo.addressLine2,
      clientInfo.addressLine3,
      clientInfo.city,
      clientInfo.stateCounty,
      clientInfo.postCode,
      clientInfo.country,
    ].filter(Boolean);
    return parts.join(", ");
  }, [clientInfo]);

  const revenueValue = useMemo(
    () => formatCurrencyNumeric(stats.totalIncome),
    [stats.totalIncome]
  );

  const revenueShift = useMemo(() => {
    const digits = revenueValue.replace(/,/g, "").length;
    if (digits <= 5) return 0;
    const shift = (digits - 5) * 5;
    return Math.min(28, shift);
  }, [revenueValue]);

  const revenueDigits = useMemo(
    () => revenueValue.replace(/,/g, "").length,
    [revenueValue]
  );

  const revenueNumberClass = useMemo(() => {
    if (revenueDigits >= 10) return "text-[22px]";
    if (revenueDigits >= 8) return "text-[24px]";
    return "text-[26px]";
  }, [revenueDigits]);

  return (
    <div className="min-h-screen bg-white pb-12 pt-6 font-sans">
      <HeaderActionsMinimal />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div
          className="rounded-2xl shadow-lg border border-gray-200 p-6 sm:p-7 mb-6 relative"
          style={{ background: theme.loginBackground }}
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full mix-blend-overlay filter blur-3xl opacity-20 -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full mix-blend-overlay filter blur-3xl opacity-20 translate-y-1/2 -translate-x-1/2" />
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-white/5 pointer-events-none" />

          <div className="relative z-10 flex flex-col lg:flex-row lg:items-stretch gap-5">
            {/* Left: Client Selector as Title */}
            <div className="relative min-w-0">
              <div className="text-white flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center backdrop-blur-sm border border-white/20 shrink-0">
                  <Building2 className="w-7 h-7 text-white shrink-0" />
                </div>
                <div className="text-left min-w-0 flex flex-col justify-center min-h-[112px] gap-1">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="flex items-center gap-2 group min-w-0"
                    >
                      <h1
                        title={client || "Select Client"}
                        className="text-[32px] font-extrabold leading-[1.1] tracking-tight group-hover:text-white/80 transition-colors break-words text-left"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          maxWidth: "520px",
                          overflow: "hidden",
                        }}
                      >
                        {client || "Select Client"}
                      </h1>
                      <ChevronDown
                        className={`w-5 h-5 text-white/50 group-hover:text-white/80 transition-all duration-200 shrink-0 ${isDropdownOpen ? "rotate-180" : ""
                          }`}
                      />
                    </button>
                    {selectedClientId && (
                      <button
                        onClick={handleEditClientClick}
                        className="p-1.5 rounded-lg hover:bg-white/10 transition-colors shrink-0"
                        title="Edit client"
                      >
                        <Edit3 className="w-4 h-4 text-white/40 hover:text-white/70" />
                      </button>
                    )}
                  </div>
                  <p className="text-white/60 text-sm">
                    {clients.length} clients in database
                  </p>
                </div>
              </div>

              {isDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => {
                      setIsDropdownOpen(false);
                      setClientSearch("");
                    }}
                  />
                  <div className="absolute top-full left-0 mt-3 bg-white rounded-xl shadow-2xl border border-gray-200 max-h-80 overflow-hidden z-50 min-w-[280px] flex flex-col">
                    <div className="sticky top-0 bg-gray-50 px-4 py-2 border-b border-gray-100">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Select a client
                      </p>
                      <input
                        type="text"
                        value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                        placeholder="Search clients..."
                        autoFocus
                        className="w-full px-3 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="overflow-y-auto flex-1">
                      {clients
                        .filter((c) =>
                          c.name.toLowerCase().includes(clientSearch.toLowerCase())
                        )
                        .map((clientObj) => (
                          <button
                            key={clientObj.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClientChange(clientObj);
                              setClientSearch("");
                            }}
                            className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-b-0 ${client === clientObj.name
                                ? "bg-indigo-50 text-indigo-700 font-semibold"
                                : "text-gray-700"
                              }`}
                          >
                            {clientObj.name}
                          </button>
                        ))}
                      {clients.filter((c) =>
                        c.name.toLowerCase().includes(clientSearch.toLowerCase())
                      ).length === 0 && (
                        <p className="px-4 py-3 text-sm text-gray-400 text-center">
                          No clients found
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Right: Stats (pushed to the right) */}
            {client && (
              <div className="flex flex-nowrap items-stretch gap-3 lg:ml-auto min-h-[76px]">
                <div className="flex items-center justify-center gap-3 px-5 py-4 rounded-2xl bg-white/[0.12] backdrop-blur-sm border border-white/[0.15] min-h-[76px] w-[170px] sm:w-[182px] shrink-0">
                  <Briefcase className="w-7 h-7 text-white/70 shrink-0" />
                  <div>
                    <p className="text-[26px] font-extrabold text-white leading-tight whitespace-nowrap">
                      {stats.totalProjects}
                    </p>
                    <p className="text-[12px] text-white/60 uppercase tracking-wide">
                      Projects
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-3 px-5 py-4 rounded-2xl bg-white/[0.12] backdrop-blur-sm border border-white/[0.15] min-h-[76px] w-[170px] sm:w-[182px] shrink-0">
                  <FileText className="w-7 h-7 text-white/70 shrink-0" />
                  <div>
                    <p className="text-[26px] font-extrabold text-white leading-tight whitespace-nowrap">
                      {stats.totalTasks}
                    </p>
                    <p className="text-[12px] text-white/60 uppercase tracking-wide">
                      Tasks
                    </p>
                  </div>
                </div>
                {canViewFinancials && (
                  <>
                    <div className="flex items-center justify-center px-5 py-4 rounded-2xl bg-white/[0.12] backdrop-blur-sm border border-white/[0.15] min-h-[76px] w-[170px] sm:w-[182px] shrink-0">
                      <div
                        className="flex items-center gap-2 w-full"
                        style={
                          revenueShift
                            ? { transform: `translateX(-${revenueShift}px)` }
                            : undefined
                        }
                      >
                        <PoundSterling className="w-7 h-7 text-white/70 shrink-0" />
                        <div className="min-w-0">
                          <p
                            className={`${revenueNumberClass} font-extrabold text-white leading-tight whitespace-nowrap`}
                          >
                            {revenueValue}
                          </p>
                          <p className="text-[12px] text-white/60 uppercase tracking-wide">
                            Revenue
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-center gap-3 px-5 py-4 rounded-2xl bg-white/[0.12] backdrop-blur-sm border border-white/[0.15] min-h-[76px] w-[170px] sm:w-[182px] shrink-0">
                      <PoundSterling className="w-7 h-7 text-white/70 shrink-0" />
                      <div>
                        <p className="text-[26px] font-extrabold text-white leading-tight whitespace-nowrap">
                          {formatCurrencyNumeric(stats.totalCost)}
                        </p>
                        <p className="text-[12px] text-white/60 uppercase tracking-wide">
                          Costs
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/3" />
                    <div className="h-3 bg-gray-200 rounded w-2/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : client ? (
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left Sidebar - Client Details */}
            <div className="lg:w-80 flex-shrink-0">
              <div className="sticky top-6">
                {/* Client Info Card */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  {/* Header Section */}
                  <div className="relative px-5 pt-6 pb-5">
                    {/* Subtle background accent */}
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-white pointer-events-none" />

                    <div className="relative flex flex-col items-center text-center">
                      {/* Avatar */}
                      <div
                        className="w-16 h-16 rounded-2xl flex items-center justify-center text-white font-bold text-2xl shadow-lg mb-4"
                        style={{ background: theme.clientAvatarGradient }}
                      >
                        {client?.charAt(0)?.toUpperCase() || "C"}
                      </div>

                      {/* Client Name */}
                      <h3
                        className="text-[22px] font-semibold text-gray-900 leading-tight mb-1"
                        style={{
                          fontFamily: "'Inter', sans-serif",
                          letterSpacing: "-0.02em",
                        }}
                      >
                        {client || "Client"}
                      </h3>

                      {/* Reference */}
                      {clientInfo.companyReference && (
                        <p className="text-xs text-gray-400 font-medium tracking-wide">
                          {clientInfo.companyReference}
                        </p>
                      )}

                      {/* Stats Row */}
                      <div className="flex items-center gap-4 mt-4">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-gray-800">
                            {stats.totalProjects}
                          </p>
                          <p className="text-[10px] text-gray-400 uppercase tracking-wider">
                            Projects
                          </p>
                        </div>
                        <div className="w-px h-8 bg-gray-200" />
                        <div className="text-center">
                          <p className="text-2xl font-bold text-gray-800">
                            {stats.totalTasks}
                          </p>
                          <p className="text-[10px] text-gray-400 uppercase tracking-wider">
                            Tasks
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contact Info Section */}
                  <div className="px-5 pb-5">
                    <div className="space-y-2">
                      {/* Phone */}
                      {clientInfo.telephoneNumber && (
                        <a
                          href={`tel:${clientInfo.telephoneNumber}`}
                          className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors group"
                        >
                          <div className="w-9 h-9 rounded-lg bg-white shadow-sm flex items-center justify-center group-hover:shadow transition-shadow">
                            <Phone className="w-4 h-4 text-gray-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">
                              {clientInfo.telephoneNumber}
                            </p>
                            <p className="text-[10px] text-gray-400 uppercase tracking-wide">
                              Phone
                            </p>
                          </div>
                        </a>
                      )}

                      {/* Email */}
                      {clientInfo.email && (
                        <a
                          href={`mailto:${clientInfo.email}`}
                          className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors group"
                        >
                          <div className="w-9 h-9 rounded-lg bg-white shadow-sm flex items-center justify-center group-hover:shadow transition-shadow">
                            <Mail className="w-4 h-4 text-gray-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">
                              {clientInfo.email}
                            </p>
                            <p className="text-[10px] text-gray-400 uppercase tracking-wide">
                              Email
                            </p>
                          </div>
                        </a>
                      )}

                      {/* Address */}
                      {fullAddress && (
                        <div className="flex items-start gap-3 p-3 rounded-xl bg-gray-50">
                          <div className="w-9 h-9 rounded-lg bg-white shadow-sm flex items-center justify-center shrink-0">
                            <MapPin className="w-4 h-4 text-gray-500" />
                          </div>
                          <div className="flex-1 min-w-0 pt-1">
                            <div className="text-xs text-gray-500 uppercase tracking-wide leading-relaxed space-y-0.5">
                              {clientInfo.addressLine1 && (
                                <p>{clientInfo.addressLine1}</p>
                              )}
                              {clientInfo.addressLine2 && (
                                <p>{clientInfo.addressLine2}</p>
                              )}
                              {clientInfo.addressLine3 && (
                                <p>{clientInfo.addressLine3}</p>
                              )}
                              {(clientInfo.city || clientInfo.stateCounty) && (
                                <p>
                                  {[clientInfo.city, clientInfo.stateCounty]
                                    .filter(Boolean)
                                    .join(", ")}
                                </p>
                              )}
                              {clientInfo.postCode && (
                                <p>{clientInfo.postCode}</p>
                              )}
                              {clientInfo.country && (
                                <p className="flex items-center gap-1.5 mt-1">
                                  <span>
                                    {getCountryFlag(clientInfo.country)}
                                  </span>
                                  <span>{clientInfo.country}</span>
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Points of Contact */}
                  {clientInfo.pointOfContacts &&
                    clientInfo.pointOfContacts.length > 0 && (
                      <div className="border-t border-gray-100 px-5 py-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                            Contacts
                          </span>
                          <span className="text-[11px] text-gray-400">
                            {clientInfo.pointOfContacts.length}
                          </span>
                        </div>
                        <div className="flex flex-col gap-2">
                          {clientInfo.pointOfContacts.map((poc, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                            >
                              <div
                                className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold text-white"
                                style={{
                                  background: theme.clientContactAvatarGradient,
                                }}
                              >
                                {poc.charAt(0).toUpperCase()}
                              </div>
                              <span className="text-sm font-medium text-gray-700">
                                {poc}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* Notes Section */}
                  {clientInfo.otherInfo && (
                    <div className="border-t border-gray-100 px-5 py-4">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                          Notes
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed pl-5">
                        {clientInfo.otherInfo}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Content - Projects */}
            <div className="flex-1 min-w-0">
              <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
                <h2 className="text-[17px] font-semibold text-gray-400 uppercase tracking-[0.15em] ml-2">
                  Projects
                </h2>
                <div className="flex flex-wrap items-center gap-0.25">
                  {STATUS_ORDER.map((status) => {
                    const count = stats.statusCounts[status] ?? 0;
                    const config = getStatusConfig(status);
                    const isSelected = statusFilter === status;
                    return (
                      <button
                        key={status}
                        onClick={() =>
                          setStatusFilter(isSelected ? null : status)
                        }
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[12px] transition-all duration-200 ${isSelected
                            ? "bg-gray-900/10 shadow-sm"
                            : "hover:bg-gray-100"
                          }`}
                      >
                        <span
                          className={`w-2 h-2 rounded-full ${config.dot}`}
                          style={{
                            backgroundColor: config.filterDotColor || undefined,
                          }}
                        ></span>
                        <span
                          className={`font-medium ${isSelected ? "text-gray-800" : "text-gray-500"
                            }`}
                        >
                          {count}
                        </span>
                        <span
                          className={
                            isSelected ? "text-gray-700" : "text-gray-400"
                          }
                        >
                          {status}
                        </span>
                      </button>
                    );
                  })}
                  {statusFilter && (
                    <button
                      onClick={() => setStatusFilter(null)}
                      className="ml-1 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      aria-label="Clear status filter"
                      title="Clear status filter"
                    >
                      <X className="w-3.5 h-3.5" strokeWidth={2} />
                    </button>
                  )}
                </div>
              </div>

              {projects.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                  <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No projects found
                  </h3>
                  <p className="text-sm text-gray-500">
                    This client doesn't have any projects yet.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {projects
                    .filter(
                      (project) =>
                        !statusFilter || project.projectStatus === statusFilter
                    )
                    .map((project) => {
                      const statusConfig = getStatusConfig(
                        project.projectStatus
                      );
                      const taskCount = project.tasks?.length || 0;
                      const nextDueDate = getNextDueDate(project);
                      const dueLabel = formatDateDisplay(nextDueDate);
                      const relativeDue = getRelativeDue(nextDueDate);
                      const topCountry = getTopCountry(project);
                      const primaryContact =
                        project.poc || clientInfo.pointOfContacts?.[0];

                      // For completed/invoiced projects
                      const completionDate = getLatestCompletionDate(project);
                      const completedAgo = getDaysAgo(completionDate);
                      const invoiceDate = toDate(project.dateForInvoice);
                      const invoicedAgo = getDaysAgo(invoiceDate);

                      return (
                        <div
                          key={project.id}
                          onClick={() => openProjectInNewTab(project.id)}
                          className="group relative bg-white rounded-xl shadow-sm hover:shadow-lg border border-gray-100 hover:border-gray-200 transition-all duration-300 cursor-pointer overflow-hidden"
                        >
                          {/* Status accent bar */}
                          <div
                            className={`absolute left-0 top-0 bottom-0 w-1 ${statusConfig.dot}`}
                            style={{
                              backgroundColor:
                                statusConfig.dotColor || undefined,
                            }}
                          />

                          <div className="pl-5 pr-5 py-5">
                            {/* Header */}
                            <div className="flex items-start justify-between gap-4 mb-5">
                              <div className="min-w-0">
                                <h3 className="text-xl font-bold text-blue-600 group-hover:text-blue-700 tracking-tight mb-1.5">
                                  {project.projectRef}
                                </h3>
                                <div className="inline-flex items-center gap-1.5 text-[12px] text-gray-500 font-medium">
                                  <span
                                    className={`w-2 h-2 rounded-full ${statusConfig.dot}`}
                                    style={{
                                      backgroundColor:
                                        statusConfig.dotColor || undefined,
                                    }}
                                  ></span>
                                  {project.projectStatus || "Status"}
                                </div>
                              </div>

                              <div className="flex flex-col items-center shrink-0">
                                <div className="w-11 h-11 rounded-full bg-gray-100 group-hover:bg-gray-200 flex items-center justify-center transition-colors">
                                  <span className="text-lg font-bold text-gray-700">
                                    {taskCount}
                                  </span>
                                </div>
                                <p className="text-[10px] text-gray-400 uppercase tracking-wide mt-1">
                                  {taskCount === 1 ? "Task" : "Tasks"}
                                </p>
                              </div>
                            </div>

                            {/* Meta info */}
                            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-gray-500 py-3 border-t border-gray-100">
                              {/* Country + Flag */}
                              <div className="flex items-center gap-1.5">
                                <CountryFlagIcon
                                  country={topCountry || project.country}
                                  size={18}
                                />
                                <span className="font-medium text-gray-600">
                                  {topCountry ||
                                    project.country ||
                                    "Location TBC"}
                                </span>
                              </div>

                              <span className="text-gray-200">•</span>

                              {/* Project Manager */}
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-md bg-gray-50 flex items-center justify-center">
                                  <User className="w-3.5 h-3.5 text-gray-400" />
                                </div>
                                <span className="font-medium text-gray-600">
                                  {project.projectManager || "Unassigned"}
                                </span>
                              </div>

                              <span className="text-gray-200">•</span>

                              {/* Due date for active projects */}
                              {(relativeDue || dueLabel) &&
                                ![
                                  "Completed",
                                  "To be invoiced",
                                  "Invoiced",
                                ].includes(project.projectStatus) && (
                                  <>
                                    <div className="flex items-center gap-2">
                                      <div className="w-6 h-6 rounded-md bg-gray-50 flex items-center justify-center">
                                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                                      </div>
                                      <span className="font-medium text-gray-600">
                                        {relativeDue || dueLabel}
                                      </span>
                                    </div>
                                    <span className="text-gray-200">•</span>
                                  </>
                                )}

                              {/* Completed date for Completed/To be invoiced */}
                              {["Completed", "To be invoiced"].includes(
                                project.projectStatus
                              ) &&
                                completedAgo && (
                                  <>
                                    <div className="flex items-center gap-2">
                                      <div className="w-6 h-6 rounded-md bg-gray-50 flex items-center justify-center">
                                        <CheckCircle className="w-3.5 h-3.5 text-gray-400" />
                                      </div>
                                      <span className="font-medium text-gray-600">
                                        Completed {completedAgo}
                                      </span>
                                    </div>
                                    <span className="text-gray-200">•</span>
                                  </>
                                )}

                              {/* Invoice date for Invoiced */}
                              {project.projectStatus === "Invoiced" &&
                                invoicedAgo && (
                                  <>
                                    <div className="flex items-center gap-2">
                                      <div className="w-6 h-6 rounded-md bg-gray-50 flex items-center justify-center">
                                        <FileText className="w-3.5 h-3.5 text-gray-400" />
                                      </div>
                                      <span className="font-medium text-gray-600">
                                        Invoiced {invoicedAgo}
                                      </span>
                                    </div>
                                    <span className="text-gray-200">•</span>
                                  </>
                                )}

                              {primaryContact && (
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded-md bg-gray-50 flex items-center justify-center">
                                    <Phone className="w-3.5 h-3.5 text-gray-400" />
                                  </div>
                                  <span className="font-medium text-gray-600">
                                    {primaryContact}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Financial Row (privileged users) */}
                            {canViewFinancials && (
                              <div className="flex items-center gap-2 pt-3 mt-1 border-t border-gray-100">
                                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50">
                                  <span className="text-[10px] text-gray-400 uppercase tracking-wide">
                                    Cost
                                  </span>
                                  <span className="text-sm font-semibold text-gray-600">
                                    {formatCurrency(project.cost || 0)}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50">
                                  <span className="text-[10px] text-gray-400 uppercase tracking-wide">
                                    Fee
                                  </span>
                                  <span className="text-sm font-semibold text-gray-600">
                                    {formatCurrency(project.income || 0)}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50">
                                  <span className="text-[10px] text-gray-400 uppercase tracking-wide">
                                    VAT
                                  </span>
                                  <span className="text-sm font-semibold text-gray-600">
                                    {project.vat || 0}%
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50">
                                  <span className="text-[10px] text-gray-400 uppercase tracking-wide">
                                    Total
                                  </span>
                                  <span className="text-sm font-semibold text-gray-600">
                                    {formatCurrency(project.amountIncVat || 0)}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No client selected
            </h3>
            <p className="text-sm text-gray-500">
              Select a client from the dropdown above to view their details.
            </p>
          </div>
        )}
      </div>

      {/* Edit Client Dialog */}
      <Dialog
        open={openEdit}
        onClose={createNoBackdropCloseHandler(handleCloseEdit)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            overflow: "hidden",
            borderRadius: "16px",
          },
        }}
      >
        {selectedClientId && (
          <EditClient clientId={selectedClientId} onClose={handleCloseEdit} />
        )}
      </Dialog>
    </div>
  );
};

export default Clients;
